/**
 * Shared MarkdownIt renderer for the app.
 *
 * markdown-it follows the CommonMark spec for emphasis (`*`, `**`, `_`, `__`):
 * a delimiter run is only treated as emphasis when it is "left/right flanking",
 * which forbids the delimiter from sitting next to Unicode punctuation.
 * CJK text almost always wraps emphasis in full-width punctuation such as
 * 「」 『』 【】 （） ，。：；！？ and half-width corner brackets ｢｣, so
 * `**「暖色」**` / `【**内容**】` / `**『引用』**` etc. silently fails to render
 * and the raw `**` markers leak into the output.
 *
 * Mixed CJK/latin content also wraps emphasis in ASCII half-width parentheses
 * right next to the delimiter — e.g. `**(0,10)**`, `**(+1 EV)**`,
 * `**(40,32)**` — which equally fails because `(` / `)` are ASCII punctuation
 * that the default flanking rule treats as breaking.
 *
 * The `cjkEmphasis` plugin below relaxes that rule for CJK punctuation AND
 * ASCII half-width parentheses by treating them as flanking-neutral (like a
 * normal letter), so emphasis works next to CJK brackets/quotes/punctuation
 * and around `( )` just as it does next to CJK letters. The override is scoped
 * to each MarkdownIt instance (it subclasses that instance's inline State), so
 * it never affects other renderers globally.
 */
import MarkdownIt from "markdown-it";
import { isWhiteSpace, isPunctChar, isMdAsciiPunct } from "markdown-it/lib/common/utils.mjs";

const CJK_IDEOGRAPH_START = 0x3400;
const CJK_IDEOGRAPH_END = 0x9fff;
const CJK_COMPAT_START = 0xf900;
const CJK_COMPAT_END = 0xfaff;
const CJK_SYMBOL_START = 0x3000;
const CJK_SYMBOL_END = 0x303f;
const FULLWIDTH_START = 0xff01;
const FULLWIDTH_END = 0xff9f; // Halfwidth and Fullwidth Forms (incl. ｡ ､ ｢｣ ｢｣ etc.)
const KANA_START = 0x3041;
const KANA_END = 0x30ff;

function isCjkChar(ch) {
  const c = ch.charCodeAt(0);
  return (
    (c >= CJK_SYMBOL_START && c <= CJK_SYMBOL_END) ||
    (c >= FULLWIDTH_START && c <= FULLWIDTH_END) ||
    (c >= KANA_START && c <= KANA_END) ||
    (c >= CJK_IDEOGRAPH_START && c <= CJK_IDEOGRAPH_END) ||
    (c >= CJK_COMPAT_START && c <= CJK_COMPAT_END)
  );
}

const PAREN_OPEN = 0x28; // (
const PAREN_CLOSE = 0x29; // )

/**
 * Whether a character should be treated as flanking-neutral for emphasis
 * (i.e. it does NOT break the CommonMark left/right flanking rules).
 *
 * CJK punctuation is already flanking-neutral: `**「…」**` behaves like
 * `**文字**`. ASCII half-width parentheses `(` `)` are extremely common
 * right next to emphasis in mixed CJK/latin content — e.g. `**(0,10)**`,
 * `**(+1 EV)**`, `**(40,32)**` — yet they are ASCII punctuation that the
 * default flanking rule treats as breaking, so `**(0,10)**` silently fails
 * to render and leaks the raw `**` markers. Treat them the same as CJK
 * punctuation so emphasis opens/closes correctly around them.
 */
function isEmphasisNeutralChar(ch) {
  const c = ch.charCodeAt(0);
  return isCjkChar(ch) || c === PAREN_OPEN || c === PAREN_CLOSE;
}

function cjkEmphasisPlugin(md) {
  // Subclass this instance's inline State so the override is scoped.
  class CjkStateInline extends md.inline.State {
    scanDelims(start, canSplitWord) {
      const max = this.posMax;
      const marker = this.src.charCodeAt(start);

      const lastChar = start > 0 ? this.src.charCodeAt(start - 1) : 0x20;

      let pos = start;
      while (pos < max && this.src.charCodeAt(pos) === marker) {
        pos++;
      }
      const count = pos - start;

      const nextChar = pos < max ? this.src.charCodeAt(pos) : 0x20;

      // CJK punctuation is treated as flanking-neutral: it does not count as
      // "Unicode punctuation" for the CommonMark emphasis flanking rules, so
      // `**「…」**` / `（**…**）` behave like `**文字**`.
      const lastCh = String.fromCharCode(lastChar);
      const nextCh = String.fromCharCode(nextChar);
      const isLastPunctChar =
        (isMdAsciiPunct(lastChar) || isPunctChar(lastCh)) &&
        !isEmphasisNeutralChar(lastCh);
      const isNextPunctChar =
        (isMdAsciiPunct(nextChar) || isPunctChar(nextCh)) &&
        !isEmphasisNeutralChar(nextCh);

      const isLastWhiteSpace = isWhiteSpace(lastChar);
      const isNextWhiteSpace = isWhiteSpace(nextChar);

      const left_flanking =
        !isNextWhiteSpace && (!isNextPunctChar || isLastWhiteSpace || isLastPunctChar);
      const right_flanking =
        !isLastWhiteSpace && (!isLastPunctChar || isNextWhiteSpace || isNextPunctChar);

      const can_open = left_flanking && (canSplitWord || !right_flanking || isLastPunctChar);
      const can_close = right_flanking && (canSplitWord || !left_flanking || isNextPunctChar);

      return { can_open, can_close, length: count };
    }
  }

  md.inline.State = CjkStateInline;
}

/**
 * Create a MarkdownIt renderer with the CJK emphasis fix applied.
 * @param {import("markdown-it").Options} [options]
 * @returns {import("markdown-it")}
 */
export function createMarkdownRenderer(options = {}) {
  const md = new MarkdownIt({
    html: false,
    breaks: false,
    linkify: true,
    ...options,
  });
  cjkEmphasisPlugin(md);
  return md;
}

// Default renderer used by the analysis result views (TaskSummaryCard,
// GapCardsPanel, gap renderers): paragraph breaks + autolink, no raw HTML.
export const markdownRenderer = createMarkdownRenderer({
  breaks: true,
  linkify: true,
});
